/**
 * Stripe Service
 *
 * Handles Stripe integration for subscription management, checkout,
 * and billing portal access.
 */

import api from '@/api';

// ============================================================================
// Configuration
// ============================================================================

let stripePromise = null;

/**
 * Get Stripe publishable key from backend
 * @returns {Promise<string>}
 */
export async function getStripeConfig() {
  try {
    const response = await api.get('/billing/config');
    return response.data;
  } catch (error) {
    console.error('Failed to get Stripe config:', error);
    throw error;
  }
}

/**
 * Load Stripe.js library and initialize with publishable key
 * @returns {Promise<Stripe>}
 */
export async function getStripe() {
  if (!stripePromise) {
    // Dynamically load Stripe.js
    if (!window.Stripe) {
      await loadStripeScript();
    }

    const config = await getStripeConfig();
    stripePromise = window.Stripe(config.publishable_key);
  }
  return stripePromise;
}

/**
 * Load Stripe.js script dynamically
 */
function loadStripeScript() {
  return new Promise((resolve, reject) => {
    if (window.Stripe) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Stripe.js'));
    document.head.appendChild(script);
  });
}

// ============================================================================
// Checkout & Portal
// ============================================================================

/**
 * Create a checkout session and redirect to Stripe Checkout
 * @param {string} priceId - The Stripe price ID
 * @param {string} [successUrl] - Custom success URL
 * @param {string} [cancelUrl] - Custom cancel URL
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function createCheckoutSession(priceId, successUrl, cancelUrl) {
  try {
    const response = await api.post('/billing/create-checkout-session', {
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });

    // Redirect to Stripe Checkout
    window.location.href = response.data.checkout_url;
    return { success: true };
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to start checkout',
    };
  }
}

/**
 * Open Stripe Customer Portal for subscription management
 * @param {string} [returnUrl] - URL to return to after portal session
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function openCustomerPortal(returnUrl) {
  try {
    const response = await api.post('/billing/create-portal-session', {
      return_url: returnUrl,
    });

    // Redirect to Stripe Customer Portal
    window.location.href = response.data.portal_url;
    return { success: true };
  } catch (error) {
    console.error('Failed to open customer portal:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to open billing portal',
    };
  }
}

// ============================================================================
// Subscription Management
// ============================================================================

/**
 * Get current subscription details
 * @returns {Promise<object|null>}
 */
export async function getSubscription() {
  try {
    const response = await api.get('/billing/subscription');
    return response.data;
  } catch (error) {
    console.error('Failed to get subscription:', error);
    return null;
  }
}

/**
 * Cancel subscription at period end
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function cancelSubscription() {
  try {
    await api.post('/billing/cancel-subscription');
    return { success: true };
  } catch (error) {
    console.error('Failed to cancel subscription:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to cancel subscription',
    };
  }
}

/**
 * Reactivate a canceled subscription
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function reactivateSubscription() {
  try {
    await api.post('/billing/reactivate-subscription');
    return { success: true };
  } catch (error) {
    console.error('Failed to reactivate subscription:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to reactivate subscription',
    };
  }
}

// ============================================================================
// Payment Methods
// ============================================================================

/**
 * Get saved payment methods
 * @returns {Promise<object[]>}
 */
export async function getPaymentMethods() {
  try {
    const response = await api.get('/billing/payment-methods');
    return response.data;
  } catch (error) {
    console.error('Failed to get payment methods:', error);
    return [];
  }
}

/**
 * Delete a payment method
 * @param {string} paymentMethodId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deletePaymentMethod(paymentMethodId) {
  try {
    await api.delete(`/billing/payment-methods/${paymentMethodId}`);
    return { success: true };
  } catch (error) {
    console.error('Failed to delete payment method:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to remove payment method',
    };
  }
}

/**
 * Set a payment method as default
 * @param {string} paymentMethodId
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function setDefaultPaymentMethod(paymentMethodId) {
  try {
    await api.post(`/billing/payment-methods/${paymentMethodId}/default`);
    return { success: true };
  } catch (error) {
    console.error('Failed to set default payment method:', error);
    return {
      success: false,
      error: error.response?.data?.detail || 'Failed to update default payment method',
    };
  }
}

// ============================================================================
// Invoices
// ============================================================================

/**
 * Get invoice history
 * @param {number} [limit=10] - Number of invoices to fetch
 * @returns {Promise<object[]>}
 */
export async function getInvoices(limit = 10) {
  try {
    const response = await api.get('/billing/invoices', {
      params: { limit },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get invoices:', error);
    return [];
  }
}

/**
 * Get upcoming invoice preview
 * @returns {Promise<object|null>}
 */
export async function getUpcomingInvoice() {
  try {
    const response = await api.get('/billing/upcoming-invoice');
    return response.data;
  } catch (error) {
    console.error('Failed to get upcoming invoice:', error);
    return null;
  }
}

// ============================================================================
// Plans
// ============================================================================

/**
 * Get available subscription plans
 * @returns {Promise<object[]>}
 */
export async function getPlans() {
  try {
    const response = await api.get('/billing/plans');
    return response.data.plans;
  } catch (error) {
    console.error('Failed to get plans:', error);
    // Return default plans as fallback
    return getDefaultPlans();
  }
}

/**
 * Default plans fallback
 */
function getDefaultPlans() {
  return [
    {
      id: 'free',
      name: 'Free',
      tier: 'free',
      description: 'For individuals and small projects',
      monthly_price: 0,
      yearly_price: 0,
      features: [
        'Up to 2 team members',
        '1,000 API calls/month',
        '10 investigations/month',
        '1 GB storage',
        'Community support',
      ],
    },
    {
      id: 'starter',
      name: 'Starter',
      tier: 'starter',
      description: 'For small teams getting started',
      monthly_price: 2900,
      yearly_price: 29000,
      features: [
        'Up to 5 team members',
        '10,000 API calls/month',
        '100 investigations/month',
        '5 GB storage',
        'Email support',
      ],
    },
    {
      id: 'professional',
      name: 'Professional',
      tier: 'professional',
      description: 'For growing teams',
      monthly_price: 9900,
      yearly_price: 99000,
      is_popular: true,
      features: [
        'Up to 10 team members',
        '100,000 API calls/month',
        '500 investigations/month',
        '10 GB storage',
        'Priority support',
        'SSO authentication',
      ],
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      tier: 'enterprise',
      description: 'For large organizations',
      monthly_price: 29900,
      yearly_price: 299000,
      features: [
        'Unlimited team members',
        'Unlimited API calls',
        'Unlimited investigations',
        '100 GB storage',
        'Dedicated support',
        'Custom integrations',
        'SLA guarantee',
      ],
    },
  ];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format amount from cents to display string
 * @param {number} amount - Amount in cents
 * @param {string} [currency='usd'] - Currency code
 * @returns {string}
 */
export function formatAmount(amount, currency = 'usd') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 0,
  }).format(amount / 100);
}

/**
 * Get card brand display name
 * @param {string} brand
 * @returns {string}
 */
export function getCardBrandName(brand) {
  const brands = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brands[brand?.toLowerCase()] || brand || 'Card';
}

/**
 * Check if URL has Stripe checkout success params
 * @returns {{success: boolean, sessionId?: string, canceled?: boolean}}
 */
export function checkCheckoutResult() {
  const params = new URLSearchParams(window.location.search);
  const success = params.get('success') === 'true';
  const canceled = params.get('canceled') === 'true';
  const sessionId = params.get('session_id');

  // Clean up URL params after reading
  if (success || canceled) {
    const url = new URL(window.location.href);
    url.searchParams.delete('success');
    url.searchParams.delete('canceled');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.toString());
  }

  return { success, sessionId, canceled };
}
